import { APP_NAME_PATTERN } from '../utils/customApp';
import type { CustomAppConfig } from '../utils/customApp';
import type { CustomDetailsConfig } from '../customApps/customDetailsConfig';
import { resolveRemoteEntryUrl } from '../config';

/**
 * Derives a full CustomDetailsConfig from a backend-provided CustomAppConfig.
 *
 * Maps the compact backend representation to the MFE coordinates needed by the
 * Module Federation runtime (remote name, manifest URL, exposed module, etc.).
 *
 * `clientId` is threaded into `resolveRemoteEntryUrl` so the manifest URL points
 * at the active tenant's apps-studio origin on per-tenant edges (e.g. fr); when
 * omitted it falls back to the shared 'client' origin. Callers must pass the
 * active clientId (from `useClientId`) — otherwise the generic custom-app path
 * 404s on per-tenant edges.
 *
 * `remoteApp` (DP-4118) is the `apps` registry's `appConfig.remoteApp`: the app
 * whose built bundle serves this one. When set, the remote name, manifest URL
 * and exposed module all come from it, while `entityName` / `testIdObject`
 * keep the requested `appName` so the route and test ids stay per-surface.
 * Several registry apps can so share one MFE (trip-ops + trip-monitoring). The
 * federation runtime keys the loaded container by the manifest's `globalName`,
 * not by the registered remote name, which is what makes the alias sound.
 * Callers get it from `useAppRegistry(clientId).remoteAppFor(appName)`.
 *
 * Returns `null` if `appName` (or `remoteApp`) does not match the allowed pattern.
 */
export function resolveAppConfig(
  app: CustomAppConfig,
  entityName: string,
  clientId?: string,
  remoteApp?: string
): CustomDetailsConfig | null {
  const { appName } = app;

  if (!APP_NAME_PATTERN.test(appName)) {
    return null;
  }

  const remote = remoteApp ?? appName;
  if (!APP_NAME_PATTERN.test(remote)) {
    return null;
  }

  return {
    entityName,
    remoteName: remote,
    remoteEntry: resolveRemoteEntryUrl(remote, clientId),
    remoteModule: `${remote}/App`,
    testIdObject: `${appName}-container`,
    defaultRouteEntity: entityName,
  };
}
